import { doc, runTransaction } from "firebase/firestore";
import { firestore } from "../configs/FirebaseConfig";
import { COMPANY_COUNTRY_CODE } from "../constants/CompanyConstants";
import { FIRESTORE_PACKAGING_STOCKS_TABLE, FIRESTORE_REGIONS_TABLE } from "../constants/FirestoreConstant";
import dayjs from "dayjs";

/**
 * Update packaging stock based on the difference between old and new scannedCrates
 * More efficient than full revert + apply as it only updates changed quantities
 * @param {Array} oldScannedCrates - Previous scannedCrates array
 * @param {Array} newScannedCrates - New scannedCrates array
 * @param {string} updatedBy - Email of user making the change
 * @returns {Promise<Object>} Result with success status and updated stocks
 */
export const updatePackagingStockDelta = async (oldScannedCrates = [], newScannedCrates = [], updatedBy = "system") => {
  try {
    // Calculate the delta for each crate type
    const deltaMap = new Map();

    // Count old quantities (these need to be added back)
    oldScannedCrates.forEach((crate) => {
      deltaMap.set(crate.barcode, (deltaMap.get(crate.barcode) || 0) + crate.quantity);
    });

    // Count new quantities (these need to be subtracted)
    newScannedCrates.forEach((crate) => {
      deltaMap.set(crate.barcode, (deltaMap.get(crate.barcode) || 0) - crate.quantity);
    });

    // Filter out zero deltas (no change needed)
    const cratesToUpdate = Array.from(deltaMap.entries())
      .filter(([barcode, delta]) => delta !== 0)
      .map(([barcode, delta]) => {
        // Find the crate info from either old or new array
        const crateInfo = [...oldScannedCrates, ...newScannedCrates].find((c) => c.barcode === barcode);
        return {
          barcode,
          delta, // positive = add to stock, negative = remove from stock
          size: crateInfo?.size || "unknown",
        };
      });

    if (cratesToUpdate.length === 0) {
      return {
        success: true,
        message: "No stock changes needed",
        updatedStocks: [],
      };
    }

    const updatedStocks = [];
    const failures = [];

    // Run one transaction per crate type. Each barcode is independent, so a failure on one
    // (e.g. a concurrent write from another session) must not stop the others from being applied.
    for (const crate of cratesToUpdate) {
      const stockRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_PACKAGING_STOCKS_TABLE,
        crate.barcode,
      );

      try {
        const result = await runTransaction(firestore, async (transaction) => {
          const stockDoc = await transaction.get(stockRef);

          if (!stockDoc.exists()) {
            throw new Error(`Packaging stock not found for barcode: ${crate.barcode}`);
          }

          const currentStock = stockDoc.data().stock;
          const newStock = currentStock + crate.delta; // delta is already correctly signed

          transaction.update(stockRef, {
            stock: newStock,
            updatedAt: dayjs().unix(),
            updatedBy,
          });

          return {
            barcode: crate.barcode,
            size: crate.size,
            oldStock: currentStock,
            newStock,
            adjustment: crate.delta,
          };
        });

        updatedStocks.push(result);
      } catch (error) {
        console.error(`Error updating packaging stock for barcode ${crate.barcode}:`, error);
        failures.push({ barcode: crate.barcode, error: error.message });
      }
    }

    if (failures.length > 0) {
      return {
        success: false,
        error: failures.map((f) => `${f.barcode}: ${f.error}`).join("; "),
        updatedStocks,
      };
    }

    return {
      success: true,
      message: `Successfully updated stock for ${cratesToUpdate.length} crate type(s)`,
      updatedStocks,
    };
  } catch (error) {
    console.error("Error updating packaging stock delta:", error);
    return {
      success: false,
      error: error.message,
      updatedStocks: [],
    };
  }
};
